/**
 * The bench's temperature model. A container holds a temperature in °C — what
 * the student sets on the burner's slider — and the coarse BAND is DERIVED
 * from it, never stored alongside it.
 *
 * The two live at different resolutions on purpose. The slider is continuous
 * because that is how a burner feels to use; the chemistry endpoint only knows
 * three bands (see api/chemistryLab.ts), and an outcome is only recomputed when
 * the derived band actually changes — otherwise every pixel of a drag would be
 * a network call for an answer that cannot have changed.
 */
import type { TemperatureBand } from './types';

export const TEMP_MIN_C = 0;
export const TEMP_MAX_C = 200;
export const TEMP_STEP_C = 5;
/** Ambient. A vessel sitting on the bench is at this, and a burner set to it
 *  or below is a burner doing nothing. */
export const ROOM_C = 25;
/** Where the band flips from room to heated. */
export const WARM_C = 40;
/** Where the band flips from heated to boiling — this is about how hard the
 *  BURNER is being driven, which is all the chemistry endpoint takes.
 *  Whether the CONTENTS boil is a different question entirely, answered by
 *  the mixture's own boiling point (see isBoiling). */
export const BOILING_C = 100;
/** Assumed boiling point when the outcome doesn't carry one — an empty vessel,
 *  or a result computed before the field existed. School mixtures are aqueous
 *  far more often than not, so water is the honest default. */
export const DEFAULT_BOILING_C = 100;

/** Whether the contents are actually boiling: the burner is past the boiling
 *  point of what is IN the vessel. Concentrated sulfuric acid (337 °C) never
 *  boils on this bench; an aqueous solution (100 °C) does. */
export function isBoiling(celsius: number, boilingPointC: number | undefined): boolean {
  return celsius >= (boilingPointC ?? DEFAULT_BOILING_C);
}

export function bandFor(celsius: number): TemperatureBand {
  if (celsius >= BOILING_C) return 'boiling';
  if (celsius >= WARM_C) return 'heated';
  return 'room';
}

/** How hard the flame is working, 0-1. Nothing above ambient means no flame. */
export function heatFraction(celsius: number): number {
  if (celsius <= ROOM_C) return 0;
  return Math.min(1, (celsius - ROOM_C) / (TEMP_MAX_C - ROOM_C));
}

export function formatTemp(celsius: number): string {
  return `${Math.round(celsius)} °C`;
}

/** What the step log records when the band changes — the band is what altered
 *  the chemistry, but the number is what the student actually chose. */
export function temperatureStepLabel(celsius: number): string {
  // Says what the student DID — set the burner — not what resulted. Whether
  // the contents boiled is the outcome's business, and is reported on the
  // step's own result line.
  return bandFor(celsius) === 'room'
    ? `Cooled to Room Temperature (${formatTemp(celsius)})`
    : `Heated to ${formatTemp(celsius)}`;
}

/**
 * Whether the mixture is boiling — from the burner, OR from the reaction.
 *
 * A reaction reports where it leaves the mixture (ChemistryLabOutcome's
 * temperatureChange.band), and until now nothing read it: the bench asked only
 * whether the BURNER had passed the boiling point, so concentrated sulphuric
 * acid poured into water — whose whole textbook warning is that it boils and
 * spits — sat there as a calm flask under a narration saying it boiled.
 *
 * The burner still counts. Either can boil it; both together is still boiling.
 */
export function isBoilingNow(
  celsius: number,
  boilingPointC: number | undefined,
  reactionBand: 'room' | 'heated' | 'boiling' | undefined,
  hasBurner: boolean,
): boolean {
  if (reactionBand === 'boiling') return true;
  return hasBurner && isBoiling(celsius, boilingPointC);
}
